// FIX-C6 : Routes Phase 5 ajoutées (CRM, Tickets, Organigramme)
// Toutes les pages Phase 5 étaient importées mais aucune route ne les exposait.
// BLOC A : Facturation (InvoicesPages) + Import/Export (ImportsPages)

import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AuthGuard } from './guards/AuthGuard'
import { GuestGuard } from './guards/GuestGuard'
import { PermissionGuard } from './guards/PermissionGuard'
import { ROUTES } from './routes'

import AppShell from '../components/layout/AppShell'
import AuthLayout from '../components/layout/AuthLayout'

import InvoicesPages from '../pages/invoices/InvoicesPages' // ← Bloc A
import ImportsPages from '../pages/imports/ImportsPages' // ← Bloc A
import DepensesPages from '../pages/depenses/DepensesPages' // ← Module Dépenses

// ── Auth ──────────────────────────────────────────────────────────────────────
import LoginPage from '../pages/auth/LoginPage'
import ForgotPasswordPage from '../pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '../pages/auth/ResetPasswordPage'
import TwoFactorPage from '../pages/auth/TwoFactorPage'
import AcceptInvitePage from '../pages/auth/AcceptInvitePage'

// ── Dashboard / Profil ───────────────────────────────────────────────────────
import DashboardPage from '../pages/dashboard/DashboardPage'
import ProfilePage from '../pages/profile/ProfilePage'

// ── Admin / RBAC ─────────────────────────────────────────────────────────────
import UsersPage from '../pages/admin/UsersPage'
import RolesPage from '../pages/admin/RolesPage'
import UserDetailPage from '../pages/admin/UserDetailPage'
import RoleDetailPage from '../pages/admin/RoleDetailPage'
import PermissionsMatrixPage from '../pages/admin/PermissionsMatrixPage'
import EntitiesPage from '../pages/admin/EntitiesPage'
import SettingsPage from '../pages/admin/SettingsPage'
import AuditLogsPage from '../pages/admin/AuditLogsPage'

// ── Comptabilité ─────────────────────────────────────────────────────────────
import AccountingDashboard from '../pages/accounting/AccountingDashboard'
import ChartOfAccountsPage from '../pages/accounting/ChartOfAccountsPage'
import JournalEntryPage from '../pages/accounting/JournalEntryPage'
import JournalListPage from '../pages/accounting/JournalListPage'
import TrialBalancePage from '../pages/accounting/TrialBalancePage'
import LedgerPage from '../pages/accounting/LedgerPage'
import FiscalYearsPage from '../pages/accounting/FiscalYearsPage'
import {
  BalanceSheetPage,
  IncomeStatementPage,
  CashFlowTftPage,
} from '../pages/accounting/FinancialReportsPage'

// ── Trésorerie ───────────────────────────────────────────────────────────────
import {
  TreasuryDashboard,
  BankAccountsPage,
  TransactionsPage,
  ReconciliationPage,
  ForecastPage,
} from '../pages/treasury/TreasuryPages'

// ── RH ───────────────────────────────────────────────────────────────────────
import EmployeesPage, {
  EmployeeDetailPage,
  EmployeeCreatePage as EmployeeNewPage,
  EmployeeEditPage,
} from '../pages/hr/employees/EmployeesPages'
import PayrollPage, { PayrollDetailPage, PayslipDetailPage } from '../pages/hr/payroll/PayrollPages'
import ContractComponentsPage from '../pages/hr/contracts/ContractComponentsPage'
import DeclarationsPage from '../pages/hr/declarations/DeclarationsPage'
import LeavesPage, { LeaveCalendarPage, LeaveRequestPage } from '../pages/hr/leaves/LeavesPages'
import AttendancePage, {
  AttendanceReportPage,
  EvaluationsPage,
  TrainingsPage,
  CreateCampaignPage,
  CampaignDetailPage,
  CreateTrainingPage,
} from '../pages/hr/attendance/AttendanceAndCareerPages'
import {
  BiometricDashboardPage,
  BiometricImportPage,
  BiometricAssociationsPage,
  BiometricMonthlyPage,
  BiometricDevicesPage,
} from '../pages/hr/biometric/BiometricPages'
import AttendanceRulesPage from '../pages/hr/attendance/AttendanceRulesPage'

// ── Projets (Phase 4) ────────────────────────────────────────────────────────
import ProjectsPage, { ProjectDetailPage, ProjectBoardPage } from '../pages/projects/ProjectsPages'
import GanttPage from '../pages/projects/GanttPage' // ← Bloc C

// ── Chat (Phase 4) ───────────────────────────────────────────────────────────
import ChatPage from '../pages/chat/ChatPage'

// ── Documents (Phase 4) ──────────────────────────────────────────────────────
import DocumentsPage from '../pages/documents/DocumentsPage'
import DocumentDetailPage from '../pages/documents/DocumentDetailPage'

// ── Réunions (Phase 4) ───────────────────────────────────────────────────────
import {
  MeetingsPage,
  MeetingDetailPage,
  NotesPage,
  NoteDetailPage,
} from '../pages/meetings/MeetingsAndNotesPages'

// ── CRM (Phase 5) — FIX-C6 ───────────────────────────────────────────────────
import PipelineConfigPage from '../pages/crm/CrmPipelineConfig'
import { CommissionsPage as CommissionsPageFull } from '../pages/crm/CrmCommissionsPage'
import OpportunityDetailPage from '../pages/crm/CrmOpportunityDetail'
import CrmDashboard, {
  PipelinePage,
  ContactsPage,
  CompaniesPage,
  ActivitiesPage,
  LeaderboardPage,

  TargetsPage,
  SequencesPage,
} from '../pages/crm/CrmPages'
import CrmAnalyticsPage from '../pages/crm/CrmAnalyticsPage' // ← Bloc C
import CommissionPlansPage from '../pages/crm/CommissionPlansPage' // ← Bloc F

// ── Tickets (Phase 5) — FIX-C6 ───────────────────────────────────────────────
import TicketsDashboard, {
  TicketsListPage,
  TicketDetailPage,
  CreateTicketPage,
  TicketCategoriesPage,
  TicketSlaPage,
} from '../pages/tickets/TicketsPages'

// ── Organigramme (Phase 5) — FIX-C6 ──────────────────────────────────────────
// FIX-M2 : Import depuis son propre fichier (pas depuis tickets.api.ts)
import OrgchartPage from '../pages/orgchart/OrgchartMain'

import AttendanceEnrichedPage from '../pages/attendance-enriched/AttendanceEnrichedPage'

// =============================================================================

export const router = createBrowserRouter([
  // ── Routes invités ──────────────────────────────────────────────────────────
  {
    element: <GuestGuard />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: ROUTES.LOGIN, element: <LoginPage /> },
          { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
        ],
      },
    ],
  },
  { path: ROUTES.RESET_PASSWORD, element: <ResetPasswordPage /> },
  { path: ROUTES.TWO_FACTOR, element: <TwoFactorPage /> },
  { path: ROUTES.ACCEPT_INVITE, element: <AcceptInvitePage /> },

  // ── Application principale ──────────────────────────────────────────────────
  {
    element: <AuthGuard />,
    children: [
      {
        element: <AppShell />,
        children: [
          // Dashboard
          { path: ROUTES.DASHBOARD, element: <DashboardPage /> },
          { path: ROUTES.PROFILE, element: <ProfilePage /> },

          // ── Admin / RBAC ──────────────────────────────────────────────────
          {
            path: ROUTES.ADMIN_USERS,
            element: (
              <PermissionGuard permission="admin:users">
                <UsersPage />
              </PermissionGuard>
            ),
          },
          {
            path: `${ROUTES.ADMIN_USERS}/:id`,
            element: (
              <PermissionGuard permission="admin:users">
                <UserDetailPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.ADMIN_ROLES,
            element: (
              <PermissionGuard permission="admin:roles">
                <RolesPage />
              </PermissionGuard>
            ),
          },
          {
            path: `${ROUTES.ADMIN_ROLES}/:id`,
            element: (
              <PermissionGuard permission="admin:roles">
                <RoleDetailPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.ADMIN_PERMISSIONS,
            element: (
              <PermissionGuard permission="admin:permissions">
                <PermissionsMatrixPage />
              </PermissionGuard>
            ),
          },
          { path: '/admin/entities', element: <EntitiesPage /> },
          {
            path: ROUTES.ADMIN_SETTINGS,
            element: (
              <PermissionGuard permission="admin:settings">
                <SettingsPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.ADMIN_AUDIT,
            element: (
              <PermissionGuard permission="admin:audit">
                <AuditLogsPage />
              </PermissionGuard>
            ),
          },

          // ── Comptabilité ──────────────────────────────────────────────────
          {
            path: ROUTES.ACCOUNTING,
            element: (
              <PermissionGuard permission="accounting:view">
                <AccountingDashboard />
              </PermissionGuard>
            ),
          },
          {
            path: '/accounting/fiscal-years',
            element: (
              <PermissionGuard permission="accounting:view">
                <FiscalYearsPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/accounting/accounts',
            element: (
              <PermissionGuard permission="accounting:view">
                <ChartOfAccountsPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.ACCOUNTING_ENTRIES,
            element: (
              <PermissionGuard permission="accounting:view">
                <JournalListPage />
              </PermissionGuard>
            ),
          },
          {
            path: `${ROUTES.ACCOUNTING_ENTRIES}/new`,
            element: (
              <PermissionGuard permission="accounting:view">
                <JournalEntryPage />
              </PermissionGuard>
            ),
          },
          {
            path: `${ROUTES.ACCOUNTING_ENTRIES}/:id`,
            element: (
              <PermissionGuard permission="accounting:view">
                <JournalEntryPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.ACCOUNTING_BALANCE,
            element: (
              <PermissionGuard permission="accounting:view">
                <TrialBalancePage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.ACCOUNTING_LEDGER,
            element: (
              <PermissionGuard permission="accounting:view">
                <LedgerPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/accounting/reports/balance-sheet',
            element: (
              <PermissionGuard permission="accounting:view">
                <BalanceSheetPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/accounting/reports/income-statement',
            element: (
              <PermissionGuard permission="accounting:view">
                <IncomeStatementPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/accounting/reports/cash-flow-tft',
            element: (
              <PermissionGuard permission="accounting:view">
                <CashFlowTftPage />
              </PermissionGuard>
            ),
          },

          // ── Trésorerie ────────────────────────────────────────────────────
          {
            path: ROUTES.TREASURY,
            element: (
              <PermissionGuard permission="treasury:view">
                <TreasuryDashboard />
              </PermissionGuard>
            ),
          },
          {
            path: '/treasury/accounts',
            element: (
              <PermissionGuard permission="treasury:view">
                <BankAccountsPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/treasury/transactions',
            element: (
              <PermissionGuard permission="treasury:view">
                <TransactionsPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.TREASURY_RECONCILIATION,
            element: (
              <PermissionGuard permission="treasury:view">
                <ReconciliationPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.TREASURY_FORECAST,
            element: (
              <PermissionGuard permission="treasury:view">
                <ForecastPage />
              </PermissionGuard>
            ),
          },

          // ── RH ────────────────────────────────────────────────────────────
          {
            path: ROUTES.HR_EMPLOYEES,
            element: (
              <PermissionGuard permission="hr:view">
                <EmployeesPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/hr/employees/new',
            element: (
              <PermissionGuard permission="hr:create">
                <EmployeeNewPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.HR_EMPLOYEE_DETAIL,
            element: (
              <PermissionGuard permission="hr:view">
                <EmployeeDetailPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/hr/employees/:id/edit',
            element: (
              <PermissionGuard permission="hr:update">
                <EmployeeEditPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.HR_PAYROLL,
            element: (
              <PermissionGuard permission="payroll:view">
                <PayrollPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/hr/payroll/:id',
            element: (
              <PermissionGuard permission="payroll:view">
                <PayrollDetailPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/hr/payroll/payslips/:id',
            element: (
              <PermissionGuard permission="payroll:view">
                <PayslipDetailPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/hr/contracts/:id/components',
            element: (
              <PermissionGuard permission="payroll:edit">
                <ContractComponentsPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/hr/payroll/declarations',
            element: (
              <PermissionGuard permission="payroll:view">
                <DeclarationsPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.HR_LEAVES,
            element: (
              <PermissionGuard permission="leaves:view">
                <LeavesPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.HR_LEAVE_CALENDAR,
            element: (
              <PermissionGuard permission="leaves:view">
                <LeaveCalendarPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/hr/leaves/request',
            element: (
              <PermissionGuard permission="leaves:request">
                <LeaveRequestPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.HR_ATTENDANCE,
            element: (
              <PermissionGuard permission="attendance:view">
                <AttendancePage />
              </PermissionGuard>
            ),
          },
          {
            path: '/hr/attendance/report',
            element: (
              <PermissionGuard permission="attendance:view">
                <AttendanceReportPage />
              </PermissionGuard>
            ),
          },

          // ── Pointage biométrique (terminal ZKTeco) ─────────────────────────
          {
            path: '/hr/biometric',
            element: (
              <PermissionGuard permission="attendance:view">
                <BiometricDashboardPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/hr/biometric/import',
            element: (
              <PermissionGuard permission="biometric:import">
                <BiometricImportPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/hr/biometric/associations',
            element: (
              <PermissionGuard permission="attendance:edit">
                <BiometricAssociationsPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/hr/biometric/monthly',
            element: (
              <PermissionGuard permission="attendance:view">
                <BiometricMonthlyPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/hr/biometric/devices',
            element: (
              <PermissionGuard permission="attendance:view">
                <BiometricDevicesPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/hr/attendance/rules',
            element: (
              <PermissionGuard permission="attendance:edit">
                <AttendanceRulesPage />
              </PermissionGuard>
            ),
          },

          // ── Carrière ──────────────────────────────────────────────────────
          {
            path: ROUTES.CAREER_EVALUATIONS,
            element: (
              <PermissionGuard permission="career:view">
                <EvaluationsPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/career/campaigns/new',
            element: (
              <PermissionGuard permission="career:view">
                <CreateCampaignPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/career/campaigns/:id',
            element: (
              <PermissionGuard permission="career:view">
                <CampaignDetailPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.CAREER_TRAININGS,
            element: (
              <PermissionGuard permission="career:view">
                <TrainingsPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/career/trainings/new',
            element: (
              <PermissionGuard permission="career:view">
                <CreateTrainingPage />
              </PermissionGuard>
            ),
          },

          // ── Projets ───────────────────────────────────────────────────────
          {
            path: ROUTES.PROJECTS,
            element: (
              <PermissionGuard permission="projects:view">
                <ProjectsPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.PROJECT_BOARD,
            element: (
              <PermissionGuard permission="projects:view">
                <ProjectBoardPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.PROJECT_DETAIL,
            element: (
              <PermissionGuard permission="projects:view">
                <ProjectDetailPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.PROJECT_GANTT,
            element: (
              <PermissionGuard permission="projects:view">
                <GanttPage />
              </PermissionGuard>
            ),
          },

          // ── Chat ──────────────────────────────────────────────────────────
          {
            path: ROUTES.CHAT,
            element: (
              <PermissionGuard permission="chat:view">
                <ChatPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.CHAT_CHANNEL,
            element: (
              <PermissionGuard permission="chat:view">
                <ChatPage />
              </PermissionGuard>
            ),
          },

          // ── Documents ─────────────────────────────────────────────────────
          {
            path: ROUTES.DOCUMENTS,
            element: (
              <PermissionGuard permission="documents:view">
                <DocumentsPage space="mine" />
              </PermissionGuard>
            ),
          },
          // Déclaré avant DOCUMENT_DETAIL (/documents/:id) pour ne pas être capturé
          {
            path: ROUTES.DOCUMENTS_SHARED,
            element: (
              <PermissionGuard permission="documents:view">
                <DocumentsPage space="shared" />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.DOCUMENT_DETAIL,
            element: (
              <PermissionGuard permission="documents:view">
                <DocumentDetailPage />
              </PermissionGuard>
            ),
          },

          // ── Réunions / Notes ──────────────────────────────────────────────
          {
            path: ROUTES.MEETINGS,
            element: (
              <PermissionGuard permission="meetings:view">
                <MeetingsPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.MEETING_DETAIL,
            element: (
              <PermissionGuard permission="meetings:view">
                <MeetingDetailPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/notes',
            element: (
              <PermissionGuard permission="meetings:view">
                <NotesPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/notes/:noteId',
            element: (
              <PermissionGuard permission="meetings:view">
                <NoteDetailPage />
              </PermissionGuard>
            ),
          },

          // ── CRM ───────────────────────────────────────────────────────────
          {
            path: ROUTES.CRM,
            element: (
              <PermissionGuard permission="crm:view">
                <CrmDashboard />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.CRM_PIPELINE,
            element: (
              <PermissionGuard permission="crm:view">
                <PipelinePage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.CRM_CONTACTS,
            element: (
              <PermissionGuard permission="crm:view">
                <ContactsPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.CRM_COMPANIES,
            element: (
              <PermissionGuard permission="crm:view">
                <CompaniesPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.CRM_ACTIVITIES,
            element: (
              <PermissionGuard permission="crm:update">
                <ActivitiesPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.CRM_LEADERBOARD,
            element: (
              <PermissionGuard permission="crm:view">
                <LeaderboardPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.CRM_COMMISSIONS,
            element: (
              <PermissionGuard permission="crm:view">
                <CommissionsPageFull />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.CRM_TARGETS,
            element: (
              <PermissionGuard permission="crm:view">
                <TargetsPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.CRM_SEQUENCES,
            element: (
              <PermissionGuard permission="crm:configure">
                <SequencesPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.CRM_ANALYTICS,
            element: (
              <PermissionGuard permission="crm:view">
                <CrmAnalyticsPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.CRM_COMMISSION_PLANS,
            element: (
              <PermissionGuard permission="crm:configure">
                <CommissionPlansPage />
              </PermissionGuard>
            ),
          },

          {
            path: '/crm/pipeline/config',
            element: (
              <PermissionGuard permission="crm:configure">
                <PipelineConfigPage />
              </PermissionGuard>
            ),
          },
          {
            path: '/crm/opportunities/:id',
            element: (
              <PermissionGuard permission="crm:view">
                <OpportunityDetailPage />
              </PermissionGuard>
            ),
          },

          // ── Tickets ───────────────────────────────────────────────────────
          {
            path: ROUTES.TICKETS,
            element: (
              <PermissionGuard permission="tickets:view">
                <TicketsDashboard />
              </PermissionGuard>
            ),
          },
          {
            path: `${ROUTES.TICKETS}/list`,
            element: (
              <PermissionGuard permission="tickets:view">
                <TicketsListPage />
              </PermissionGuard>
            ),
          },
          {
            path: `${ROUTES.TICKETS}/new`,
            element: (
              <PermissionGuard permission="tickets:create">
                <CreateTicketPage />
              </PermissionGuard>
            ),
          },
          {
            path: `${ROUTES.TICKETS}/categories`,
            element: (
              <PermissionGuard permission="tickets:view">
                <TicketCategoriesPage />
              </PermissionGuard>
            ),
          },
          {
            path: `${ROUTES.TICKETS}/sla`,
            element: (
              <PermissionGuard permission="tickets:view">
                <TicketSlaPage />
              </PermissionGuard>
            ),
          },
          {
            path: ROUTES.TICKET_DETAIL,
            element: (
              <PermissionGuard permission="tickets:view">
                <TicketDetailPage />
              </PermissionGuard>
            ),
          },

          // ── Organigramme ──────────────────────────────────────────────────
          {
            path: ROUTES.ORGCHART,
            element: (
              <PermissionGuard permission="orgchart:view">
                <OrgchartPage />
              </PermissionGuard>
            ),
          },

          // ── Facturation ───────────────────────────────────────────────────
          {
            path: '/invoices/*',
            element: (
              <PermissionGuard permission="invoices:view">
                <InvoicesPages />
              </PermissionGuard>
            ),
          },

          // ── Dépenses (notes de frais) ─────────────────────────────────────
          {
            path: '/depenses/*',
            element: (
              <PermissionGuard permission="depenses:read">
                <DepensesPages />
              </PermissionGuard>
            ),
          },

          // ── Import / Export ───────────────────────────────────────────────
          {
            path: '/imports/*',
            element: (
              <PermissionGuard permission="imports:view">
                <ImportsPages />
              </PermissionGuard>
            ),
          },

          // ── Pointage Enrichi (M9) ─────────────────────────────────
          {
            path: ROUTES.ATTENDANCE_ENRICHED,
            element: (
              <PermissionGuard permission="attendance_enriched:view">
                <AttendanceEnrichedPage />
              </PermissionGuard>
            ),
          },
        ],
      },
    ],
  },

  // ── Fallback ─────────────────────────────────────────────────────────────────
  { path: '*', element: <Navigate to={ROUTES.LOGIN} replace /> },
])
